/**
 * Cart Context
 * This file creates a context for managing the shopping cart state
 * 
 * @module context/CartContext
 * @requires react
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

/**
 * Context for cart data and operations
 * @type {React.Context}
 */
const CartContext = createContext();

/**
 * Custom hook to use the cart context
 * @returns {Object} Cart context value
 */
export const useCart = () => useContext(CartContext);

/**
 * Cart Provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Cart provider component
 */
export const CartProvider = ({ children }) => {
  // State for cart data
  const [cart, setCart] = useState({ items: [] });
  // State for loading status
  const [loading, setLoading] = useState(false);
  // State for error messages
  const [error, setError] = useState(null);

  /**
 * Fetch the cart data from the API
 */
const fetchCart = async () => {
  try {
    setLoading(true);
    setError(null);
    
    const response = await api.get('/api/cart');
    console.log('Fetched cart from context:', response.data); // Debug log
    console.log('Cart items count:', response.data.items?.length); // Debug log
    setCart(response.data);
  } catch (err) {
    console.error('Error fetching cart:', err);
    setError('Failed to load cart');
  } finally {
    setLoading(false);
  }
};

  /**
   * Add an item to the cart
   * @param {string} productId - ID of the product to add
   * @param {number} quantity - Quantity to add (default: 1)
   */
  const addToCart = async (productId, quantity = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/cart', { productId, quantity});
      setCart(response.data);
      
      // You can add a success notification here if you want
      
    } catch (err) {
      console.error('Error adding to cart:', err);
      setError('Failed to add item to cart');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Remove an item from the cart
   * @param {string} productId - ID of the product to remove
   */
  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.delete(`/api/cart/${productId}`);
      setCart(response.data);
      
    } catch (err) {
      console.error('Error removing from cart:', err);
      setError('Failed to remove item from cart');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update the quantity of an item in the cart
   * @param {string} productId - ID of the product to update
   * @param {number} quantity - New quantity
   */
  const updateQuantity = async (productId, quantity) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put(`/api/cart/${productId}`, { quantity });
      setCart(response.data);
      
    } catch (err) {
      console.error('Error updating cart:', err);
      setError('Failed to update item quantity');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calculate the total number of items in the cart
   * @returns {number} Total item count
   */
  const getItemCount = () => {
    return Array.isArray(cart?.items)
  ? cart.items.reduce((total, item) => total + item.quantity, 0)
  : 0;
  };

  /**
   * Calculate the total price of the cart
   * @returns {number} Total price
   */
  const getTotal = () => {
    return Array.isArray(cart?.items)
    ? cart.items.reduce((total, item) => total + (item.price * item.quantity), 0)
    : 0;  
  };

  // Fetch cart data when the component mounts
  useEffect(() => {
    fetchCart();
  }, []);

  // Value to be provided by the context
  const value = {
    cart,
    loading,
    error,
    addToCart,
    removeFromCart,
    updateQuantity,
    getItemCount,
    getTotal,
    fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;